import { GetServerSideProps } from 'next'
import { SummaryDetailPage } from '@/features/summary/components/SummaryDetail/SummaryDetailPage'
import { ApiClient, getHeaders } from '@/libs/api'
import { CourseId, GetSummaryDetailResponse, UserInfo } from '@/types'
import { isErrorResponse } from '@/types/error'
import { BookmarkResponse } from '@/types/bookmark'
import { GetFavoriteResponse } from '@/types/favorite'
import { GetThemeSummaryResult, SchoolSummaryStatus } from '@/types/school'
import { GetMypageStatResponse } from '@/types/mypage'
import auth from '@/utils/auth'
export default SummaryDetailPage

/**
 * 新フロントで描画するにあたり、タグを微調整
 * admin側で事前変換しているため、このタイミングでフロント向けに微調整する
 *
 * @param text HTMLタグを含むテキスト
 * @returns 最適化後のHTMLタグを含むテキスト
 */
const replaceTag = (text: string): string => {
  text = text.replaceAll('<h4>', '<h4><span>')
  text = text.replaceAll('</h4>', '</span></h4>')
  text = text.replaceAll(
    '<div class="center">',
    '<div class="contents-image align-center">'
  )
  return text
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { req, res, query } = ctx
  const {
    summary_id: summaryId,
    theme_id: themeId,
    school_summary_id: schoolSummaryId,
    code,
  } = query
  const client = new ApiClient(req, res)
  const v3Client = new ApiClient(req, res).v3()
  const isGuest = (await auth.isLogin(req, res)) ? false : true
  const headers = getHeaders(req, isGuest)
  try {
    const summary = await client.get<GetSummaryDetailResponse>(
      code
        ? `/summary/detail/${summaryId}?code=${code}`
        : `/summary/detail/${summaryId}`,
      null,
      { headers }
    )

    if (code) {
      // 不正なcodeや紐づくsummary_idではなかった場合、通常の要約ページを表示する
      if (isErrorResponse(summary)) {
        return { props: {}, redirect: { destination: `/summary/${summaryId}` } }
      }
      summary.summary.body = replaceTag(summary.summary.body)
      return {
        props: {
          userInfo: null,
          summaryDetail: summary,
          bookmarkResponse: null,
          favoriteResponse: null,
          schoolStatus: null,
          code,
        },
      }
    }

    if (isErrorResponse(summary)) {
      // 要約情報を取得できなかった場合
      return { props: {}, redirect: { destination: '/' } }
    }

    summary.summary.body = replaceTag(summary.summary.body)

    const userInfo = await client.get<UserInfo>('/user/info', {
      check_school_status: 'true',
    })
    if (!userInfo.result) {
      // 非会員の場合
      return {
        props: {
          userInfo,
          summaryDetail: summary,
          bookmarkResponse: null,
          favoriteResponse: null,
          schoolStatus: null,
        },
      }
    }
    let readThisMonth = 0
    if (userInfo.course.id == CourseId.silver) {
      // シルバーの場合
      const response = await client.get<GetMypageStatResponse>(
        '/user/mypage/stat'
      )
      const thisMonth = response.mypage.months.slice(-1)[0]
      readThisMonth = thisMonth
        ? thisMonth.read.paid + thisMonth.reading.paid
        : 0
    }
    const bookmark = await client.get<BookmarkResponse>(
      `/bookmark/${summaryId}`
    )
    const favorite = await client.get<GetFavoriteResponse>(
      `/favorite/${summaryId}`
    )
    const schoolStatus: SchoolSummaryStatus = {
      fromSchool: false,
      isComplete: false,
      themeId: themeId ? themeId.toString() : '',
      schoolSummaryId: parseInt(schoolSummaryId as string),
    }
    // スクール利用かつスクール経由の場合
    if (userInfo.use_school && themeId && schoolSummaryId) {
      schoolStatus.fromSchool = true
      const schoolResponse = await v3Client.get<GetThemeSummaryResult>(
        `/school/themes/${themeId}/summaries/${schoolSummaryId}/done`
      )
      // 振り返り完了しているか？
      schoolStatus.isComplete = schoolResponse?.is_complete ?? false
    }
    return {
      props: {
        userInfo,
        summaryDetail: summary,
        bookmarkResponse: bookmark,
        favoriteResponse: favorite,
        schoolStatus,
        readThisMonth,
      },
    }
  } catch (e) {
    console.error(e)
    return { props: {}, redirect: { destination: '/' } }
  }
}
